// frontend/src/pages/LandingPage/TermsPage.jsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, Scale, AlertCircle } from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';

const TermsPage = () => {
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);
    const currentDate = new Date().toLocaleDateString('pt-BR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });

    const sections = [
        {
            id: 1,
            title: 'Introdução',
            content: 'Este documento ("Termos de Uso") rege o acesso e a utilização da plataforma Higeia.tech ("Plataforma"), um software como serviço (SaaS) dedicado à gestão de prontuários eletrônicos de pacientes, agendamentos, tarefas de cuidado e indicadores de saúde, de propriedade da Higeia.tech. Ao acessar ou utilizar a Plataforma, o Usuário concorda com estes Termos.',
            icon: FileText
        },
        {
            id: 2,
            title: 'Definições',
            content: [
                { term: 'Plataforma', definition: 'Software Higeia.tech e suas funcionalidades.' },
                { term: 'Usuário/Contratante', definition: 'Pessoa jurídica (Clínica, Hospital, Organização de Saúde) que contrata o serviço.' },
                { term: 'Usuário Final/Profissional', definition: 'Indivíduos (médicos, enfermeiros, administradores) autorizados pelo Usuário a acessar a Plataforma.' },
                { term: 'Dados de Saúde (Dados Sensíveis)', definition: 'Informações de prontuário, histórico, sinais vitais, medicação e condição médica dos Pacientes.' },
                { term: 'LGPD', definition: 'Lei Geral de Proteção de Dados (Lei nº 13.709/2018).' }
            ],
            icon: Scale
        },
        {
            id: 3,
            title: 'Objeto do Serviço',
            content: 'O Higeia.tech é uma solução SaaS que fornece ferramentas para:',
            items: [
                'Gestão de Prontuários Eletrônicos (PEP)',
                'Gerenciamento de Agendas e Tarefas de Cuidados',
                'Monitoramento de Indicadores de Saúde e Financeiros do Paciente'
            ],
            icon: FileText
        },
        {
            id: 4,
            title: 'Responsabilidades do Usuário (Contratante)',
            items: [
                {
                    subtitle: '4.1. Licenciamento e Autorização',
                    text: 'O Usuário é o único responsável por obter e manter todas as licenças e autorizações profissionais necessárias para o exercício das atividades dos seus Usuários Finais.'
                },
                {
                    subtitle: '4.2. Dados de Pacientes',
                    text: 'O Usuário é o Controlador dos Dados de Saúde inseridos na Plataforma. O Higeia.tech atua apenas como Operador desses dados, processando-os estritamente conforme as instruções do Usuário.'
                },
                {
                    subtitle: '4.3. Conformidade Legal',
                    text: 'O Usuário se compromete a cumprir integralmente a LGPD e o Código de Ética de suas respectivas categorias profissionais, sendo o único responsável por garantir o consentimento dos Pacientes para o tratamento de seus Dados de Saúde na Plataforma.'
                },
                {
                    subtitle: '4.4. Acesso e Senhas',
                    text: 'O Usuário é responsável pela segurança e confidencialidade das credenciais de acesso de seus Usuários Finais.'
                }
            ],
            icon: Shield
        },
        {
            id: 5,
            title: 'Propriedade Intelectual e Uso da Plataforma',
            items: [
                {
                    subtitle: '5.1. Propriedade',
                    text: 'Todos os direitos de propriedade intelectual da Plataforma (código-fonte, design, logotipos, etc.) pertencem exclusivamente ao Higeia.tech.'
                },
                {
                    subtitle: '5.2. Licença de Uso',
                    text: 'É concedida ao Usuário uma licença de uso limitada, não exclusiva e intransferível da Plataforma durante o período de vigência do plano contratado.'
                },
                {
                    subtitle: '5.3. Restrições',
                    text: 'O Usuário não pode copiar, modificar, revender, sublicenciar ou realizar engenharia reversa da Plataforma.'
                }
            ],
            icon: FileText
        },
        {
            id: 6,
            title: 'Nível de Serviço (SLA) e Disponibilidade',
            items: [
                {
                    subtitle: '6.1. Disponibilidade',
                    text: 'O Higeia.tech buscará manter a Plataforma disponível 99.5% do tempo, exceto por manutenções programadas.'
                },
                {
                    subtitle: '6.2. Manutenções',
                    text: 'O Higeia.tech se reserva o direito de realizar manutenções programadas, comunicando o Usuário com antecedência razoável.'
                }
            ],
            icon: Shield
        },
        {
            id: 7,
            title: 'Cobrança e Pagamento',
            items: [
                {
                    subtitle: '7.1. Cobrança',
                    text: 'Os serviços são cobrados com base no plano e módulos contratados, conforme a Tabela de Preços vigente.'
                },
                {
                    subtitle: '7.2. Falta de Pagamento',
                    text: 'A falta de pagamento pode resultar na suspensão temporária ou no encerramento definitivo do acesso à Plataforma.'
                }
            ],
            icon: Scale
        },
        {
            id: 8,
            title: 'Rescisão',
            items: [
                {
                    subtitle: '8.1. Vigência',
                    text: 'Estes Termos permanecem em vigor enquanto o Usuário mantiver um plano de assinatura ativo.'
                },
                {
                    subtitle: '8.2. Rescisão por Violação',
                    text: 'O Higeia.tech pode rescindir o contrato imediatamente em caso de violação grave destes Termos, incluindo, mas não se limitando, ao uso indevido de Dados Sensíveis.'
                },
                {
                    subtitle: '8.3. Exclusão de Dados',
                    text: 'Em caso de rescisão, o Higeia.tech garantirá o período de 30 dias para que o Usuário realize o backup integral de seus dados. Após este período, os dados serão permanentemente excluídos da Plataforma, em conformidade com as obrigações legais de retenção aplicáveis ao Usuário.'
                }
            ],
            icon: AlertCircle
        },
        {
            id: 9,
            title: 'Disposições Finais',
            items: [
                {
                    subtitle: '9.1. Limitação de Responsabilidade',
                    text: 'O Higeia.tech não se responsabiliza por decisões médicas tomadas pelo Usuário ou Usuário Final com base nas informações contidas na Plataforma.'
                },
                {
                    subtitle: '9.2. Foro',
                    text: 'Fica eleito o Foro da Comarca de Pelotas/RS para dirimir quaisquer dúvidas ou litígios decorrentes destes Termos, com renúncia expressa a qualquer outro.'
                }
            ],
            icon: Scale
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main className="py-12 sm:py-16">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    
                    {/* Cabeçalho */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-higeia-light rounded-full mb-6">
                            <FileText className="h-8 w-8 text-higeia-dark" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-4">
                            Termos de{' '}
                            <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                Uso
                            </span>
                        </h1>
                        <p className="text-gray-600">
                            Última atualização: {currentDate}
                        </p>
                    </motion.div>

                    {/* Aviso Importante */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                        className="bg-gradient-to-r from-higeia-dark to-higeia-medium rounded-2xl p-6 text-white mb-12 shadow-xl"
                    >
                        <div className="flex items-start gap-4">
                            <AlertCircle className="h-6 w-6 flex-shrink-0 mt-1" />
                            <div>
                                <h3 className="font-bold text-lg mb-2">Aviso Importante</h3>
                                <p className="text-white/90 text-sm leading-relaxed">
                                    Ao acessar ou utilizar a plataforma Higeia.tech, você concorda com estes Termos de Uso. 
                                    Recomendamos a leitura atenta deste documento antes de utilizar nossos serviços.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Seções dos Termos */}
                    <div className="space-y-8">
                        {sections.map((section, index) => {
                            const Icon = section.icon;
                            return (
                                <motion.div
                                    key={section.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                                    className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                                >
                                    {/* Título da Seção */}
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex-shrink-0 w-12 h-12 bg-higeia-light rounded-xl flex items-center justify-center">
                                            <Icon className="h-6 w-6 text-higeia-dark" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-gray-900">
                                            {section.id}. {section.title}
                                        </h2>
                                    </div>

                                    {/* Conteúdo da Seção */}
                                    <div className="prose prose-gray max-w-none">
                                        {/* Conteúdo simples (texto) */}
                                        {typeof section.content === 'string' && (
                                            <p className="text-gray-700 leading-relaxed mb-4">
                                                {section.content}
                                            </p>
                                        )}

                                        {/* Lista de definições */}
                                        {Array.isArray(section.content) && section.content[0]?.term && (
                                            <dl className="space-y-4">
                                                {section.content.map((item, idx) => (
                                                    <div key={idx} className="border-l-4 border-higeia-light pl-4">
                                                        <dt className="font-bold text-gray-900 mb-1">
                                                            {item.term}:
                                                        </dt>
                                                        <dd className="text-gray-700">
                                                            {item.definition}
                                                        </dd>
                                                    </div>
                                                ))}
                                            </dl>
                                        )}

                                        {/* Lista de itens simples */}
                                        {section.items && Array.isArray(section.items) && typeof section.items[0] === 'string' && (
                                            <ul className="space-y-3 list-none">
                                                {section.items.map((item, idx) => (
                                                    <li key={idx} className="flex items-start gap-3">
                                                        <span className="flex-shrink-0 w-2 h-2 bg-higeia-dark rounded-full mt-2" />
                                                        <span className="text-gray-700">{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* Lista de itens com subtítulos */}
                                        {section.items && Array.isArray(section.items) && typeof section.items[0] === 'object' && (
                                            <div className="space-y-6">
                                                {section.items.map((item, idx) => (
                                                    <div key={idx} className="border-l-4 border-higeia-light pl-4">
                                                        <h4 className="font-bold text-gray-900 mb-2">
                                                            {item.subtitle}
                                                        </h4>
                                                        <p className="text-gray-700 leading-relaxed">
                                                            {item.text}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Rodapé do Documento */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.2, duration: 0.6 }}
                        className="mt-12 text-center p-8 bg-white rounded-2xl shadow-lg border border-gray-100"
                    >
                        <p className="text-gray-600 mb-4">
                            Em caso de dúvidas sobre estes Termos de Uso, entre em contato conosco através de:
                        </p>
                        <a 
                            href="mailto:contato@higeia.tech"
                            className="text-higeia-dark font-semibold hover:text-higeia-medium transition-colors"
                        >
                            contato@higeia.tech
                        </a>
                    </motion.div>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default TermsPage;

